// Reporte semanal al dueño (migración 20261016a_owner_weekly_report.sql). Arma el texto que se manda por WhatsApp a partir de las cifras que devuelve
// owner_report_data. Es la misma lógica de scripts/n8n_code/reporte_semanal_mensaje.js (la prueba src/utils/__tests__/ownerReportWorkflow.test.js las compara).
// La página «Reporte semanal» lo usa para mostrar un ejemplo; las sugerencias las escribe la IA en n8n y aquí no aparecen.

const MONTHS = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

export const money = (n) => {
  const v = Math.round(Number(n) || 0);
  return `${v < 0 ? '-' : ''}$${String(Math.abs(v)).replace(/\B(?=(\d{3})+(?!\d))/g, '.')}`;
};

const num = (v) => Number(v) || 0;

// from y to son fechas «AAAA-MM-DD»; to es exclusivo (el lunes siguiente). Ej.: 2026-10-05 → 2026-10-12 = «5–11 oct».
export const weekLabel = (from, to) => {
  const [y1, m1, d1] = String(from).slice(0, 10).split('-').map(Number);
  const [y2, m2, d2] = String(to).slice(0, 10).split('-').map(Number);
  if (![y1, m1, d1, y2, m2, d2].every(Number.isFinite)) return '';
  const end = new Date(Date.UTC(y2, m2 - 1, d2 - 1));
  const ed = end.getUTCDate();
  const em = end.getUTCMonth();
  return em === m1 - 1 ? `${d1}–${ed} ${MONTHS[em]}` : `${d1} ${MONTHS[m1 - 1]} – ${ed} ${MONTHS[em]}`;
};

const deltaCount = (cur, prev) => {
  const d = cur - prev;
  return d ? ` (${d > 0 ? '▲' : '▼'} ${Math.abs(d)})` : '';
};

const deltaMoney = (cur, prev) => {
  if (!(prev > 0) || cur === prev) return '';
  const p = Math.round(((cur - prev) / prev) * 100);
  return p ? ` (${p > 0 ? '▲' : '▼'} ${Math.abs(p)} %)` : '';
};

const SOURCES = [['pedidos', 'Pedidos'], ['citas', 'Citas'], ['ventas', 'Otras ventas']];

// Texto del reporte sin las sugerencias. report = lo que devuelve owner_report_data (o el campo data de owner_reports).
export const buildReportMessage = (report, businessName = '') => {
  const c = (report && report.current) || {};
  const p = (report && report.previous) || {};
  const name = String(businessName || '').replace(/\s+/g, ' ').trim().slice(0, 60);
  const lines = [`📊 Resumen semanal${name ? ` · ${name}` : ''} · ${weekLabel(report.from, report.to)}`, ''];
  lines.push(`💬 Conversaciones: ${num(c.conversations)}${deltaCount(num(c.conversations), num(p.conversations))} · Clientes nuevos: ${num(c.new_customers)}${deltaCount(num(c.new_customers), num(p.new_customers))}`);
  const n = num(c.sold_count);
  lines.push(`💰 Vendido: ${money(c.sold)}${deltaMoney(num(c.sold), num(p.sold))} en ${n} ${n === 1 ? 'venta' : 'ventas'}`);
  const bySource = c.sold_by_source || {};
  const parts = SOURCES.filter(([k]) => bySource[k] && num(bySource[k].amount) > 0).map(([k, label]) => `${label} ${money(bySource[k].amount)}`);
  if (parts.length > 1) lines.push(`   ${parts.join(' · ')}`);
  lines.push(`📥 Cobrado: ${money(c.collected)}${deltaMoney(num(c.collected), num(p.collected))}`);
  lines.push(`📤 Gastos pagados: ${money(c.expenses_paid)}${deltaMoney(num(c.expenses_paid), num(p.expenses_paid))}`);
  if (num(c.ads_spend) > 0) {
    const conv = num(c.ads_conversations);
    lines.push(`📣 Publicidad: ${money(c.ads_spend)}${deltaMoney(num(c.ads_spend), num(p.ads_spend))} · ${conv} ${conv === 1 ? 'conversación' : 'conversaciones'}${conv > 0 ? ` · ${money(num(c.ads_spend) / conv)} por conversación` : ''}`);
  }
  lines.push(`✅ Resultado de caja: ${money(c.cash_result)}`);
  lines.push(`🧾 Por cobrar hoy: ${money(report.receivable_total)}`);
  if (num(c.quotes_made) + num(c.quotes_sent) + num(c.quotes_accepted) > 0) {
    lines.push(`📄 Cotizaciones: ${num(c.quotes_made)} hechas · ${num(c.quotes_sent)} enviadas · ${num(c.quotes_accepted)} aceptadas${num(c.quotes_accepted) > 0 ? ` (${money(c.quotes_accepted_amount)})` : ''}`);
  }
  const q = num(report.bot_questions_open);
  if (q > 0) lines.push(`❓ ${q === 1 ? 'Hay 1 pregunta del bot sin responder' : `Hay ${q} preguntas del bot sin responder`} (Bot y sitio → Preguntas del bot).`);
  return lines.join('\n');
};

// Cifras en texto plano para la IA: solo números agregados, sin nombres ni teléfonos de clientes.
export const buildAiFacts = (report) => {
  const c = (report && report.current) || {};
  const p = (report && report.previous) || {};
  const pair = (label, key, isMoney) => `- ${label}: ${isMoney ? money(c[key]) : num(c[key])} (semana anterior: ${isMoney ? money(p[key]) : num(p[key])})`;
  return [
    pair('Conversaciones', 'conversations'),
    pair('Clientes nuevos', 'new_customers'),
    pair('Ventas (cantidad)', 'sold_count'),
    pair('Vendido', 'sold', true),
    pair('Cobrado', 'collected', true),
    pair('Gastos pagados', 'expenses_paid', true),
    pair('Resultado de caja', 'cash_result', true),
    ...(num(c.ads_spend) > 0 ? [pair('Gasto en publicidad (Meta)', 'ads_spend', true), `- Conversaciones que llegaron por anuncios: ${num(c.ads_conversations)}`] : []),
    `- Por cobrar hoy: ${money(report.receivable_total)}`,
    `- Cotizaciones hechas: ${num(c.quotes_made)}, enviadas: ${num(c.quotes_sent)}, aceptadas: ${num(c.quotes_accepted)}`,
    `- Preguntas del bot sin responder: ${num(report.bot_questions_open)}`
  ].join('\n');
};

// Deja de lo que escribió la IA solo viñetas cortas y limpias (máximo 3). Devuelve '' si no sirve nada.
export const cleanSuggestions = (raw) => {
  const out = [];
  for (const line of (typeof raw === 'string' ? raw : '').split(/\r?\n/)) {
    const t = line.replace(/^\s*(?:[-•*·]|\d+[.)])\s*/, '').replace(/[*_`#]/g, '').replace(/\s+/g, ' ').trim();
    if (t.length < 15 || /https?:|www\.|@|\d{7,}/i.test(t)) continue;
    out.push(`• ${t.slice(0, 160)}`);
    if (out.length === 3) break;
  }
  return out.join('\n');
};
